import type { BillingInterval, PlanId, SubscriptionStatus } from './types';

/**
 * Planes y limites de uso.
 *
 * El asistente se paga por token, asi que es el unico coste variable del
 * producto: si no se acota, un tenant curioso puede costar mas de lo que paga.
 * Aqui vive la unica fuente de verdad de esos limites; el API los aplica y el
 * frontend los muestra.
 */

export interface PlanDefinition {
  id: PlanId;
  name: string;
  /** Precio mensual sin IVA */
  pricePerMonth: number;
  /** Mensajes al asistente incluidos al mes. null = sin limite */
  assistantMessagesPerMonth: number | null;
  /**
   * Escaneos de factura incluidos al mes. 0 = el plan no lo trae y hace falta
   * contratar el modulo. Ver ADDONS.
   */
  receiptScansPerMonth: number;
  maxLocations: number;
  maxRecipes: number | null;
  maxUsers: number;
  features: string[];
}

/** Días de prueba gratuita (los Founder no tienen: pagan desde el primer día). */
export const TRIAL_DAYS = 14;

/* ------------------------------------------------------------------ */
/* Periodo de cobro y oferta de lanzamiento (pricing de sep 2026)      */
/* ------------------------------------------------------------------ */

/**
 * Anual: se pagan 9 meses y se usan 12 (Alex, «Pricing 2026», 15 sep 2026).
 * El precio anual sale de aqui y del mensual; no es una cifra aparte que
 * pueda descuadrarse.
 */
export const ANNUAL_MONTHS_CHARGED = 9;

/**
 * Oferta de lanzamiento, SOLO en mensual: 50 % los tres primeros meses y
 * despues el precio normal. No se acumula con el anual: quien paga 9 meses
 * ya lleva su descuento. En Stripe es un cupon `repeating` de 3 meses que el
 * checkout aplica solo; aqui vive para que la landing, Ajustes y el MRR
 * cuenten lo mismo.
 */
export const LAUNCH_DISCOUNT = { percent: 50, months: 3 } as const;

/** Precio del año entero (9 meses), sin impuestos. */
export function pricePerYear(plan: PlanId): number {
  return (PLANS[plan]?.pricePerMonth ?? 0) * ANNUAL_MONTHS_CHARGED;
}

/** Lo que sale cada mes en anual (441/12 = 36,75), sin impuestos. */
export function annualEquivalentPerMonth(plan: PlanId): number {
  return Math.round((pricePerYear(plan) / 12) * 100) / 100;
}

/** Lo que se ahorra al año frente a doce meses sueltos (147 / 237). */
export function annualSavings(plan: PlanId): number {
  return (PLANS[plan]?.pricePerMonth ?? 0) * 12 - pricePerYear(plan);
}

/** Primeros meses de la oferta de lanzamiento (24,50 / 39,50). */
export function launchPricePerMonth(plan: PlanId): number {
  return Math.round((PLANS[plan]?.pricePerMonth ?? 0) * (1 - LAUNCH_DISCOUNT.percent / 100) * 100) / 100;
}

/** Cuanto aporta el plan al MRR segun el periodo: en anual, el año entre doce. */
export function planMrr(plan: PlanId, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? annualEquivalentPerMonth(plan) : (PLANS[plan]?.pricePerMonth ?? 0);
}

/**
 * Los extras (escaner, usuario, local) van con el periodo del plan: Stripe no
 * deja mezclar intervalos en una suscripcion. Y en anual se pagan tambien 9
 * meses por 12 (Pol, 15 sep 2026): un solo mensaje, «en anual todo se paga
 * 9 meses». Esta es la unica conversion; el importe mensual es el de lista.
 */
export function extraPricePer(monthly: number, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? monthly * ANNUAL_MONTHS_CHARGED : monthly;
}

/** Lo que aporta un extra al MRR: en anual, sus 9 meses entre doce. */
export function extraMrr(monthly: number, interval: BillingInterval = 'month'): number {
  return interval === 'year' ? Math.round(((monthly * ANNUAL_MONTHS_CHARGED) / 12) * 100) / 100 : monthly;
}

/**
 * Días que damos para arreglar un cobro fallido antes de tocar nada (mejora
 * 69, Alex, 8 sep 2026). Una tarjeta caducada o un banco que bloquea un cargo
 * no es un impago: es un trámite de dos minutos que el cliente todavía no
 * sabe que tiene pendiente. Durante estos días la cuenta funciona entera y se
 * avisa por correo y dentro de la app; después pasa a solo lectura, nunca se
 * borra nada. Ver `estadoDeImpago`.
 */
export const DIAS_GRACIA_IMPAGO = 5;

/** En qué punto del impago está una cuenta. */
export type EstadoImpago =
  /** Al corriente: ni hay cobro fallido, ni queda rastro de uno. */
  | 'al_dia'
  /** Cobro fallido, dentro de los días de gracia: acceso completo y aviso. */
  | 'gracia'
  /** Se agotaron los días: la cuenta entra en solo lectura. */
  | 'solo_lectura';

export interface ImpagoStatus {
  estado: EstadoImpago;
  /** Días completos que quedan de margen (0 cuando ya se agotó). */
  diasRestantes: number;
  /** Fin del margen en ISO, tal cual se guardó. */
  graceEndsAt?: string;
}

/**
 * Traduce el estado guardado en el tenant a lo que ve el usuario. Vive en
 * shared porque lo usan las dos capas: la API para cerrar la escritura y la
 * web para pintar el aviso, y tienen que contar exactamente lo mismo.
 *
 * `past_due` sin `graceEndsAt` son las cuentas anteriores a esta mejora: se
 * tratan como en gracia con el margen entero, que es lo justo — nunca han
 * recibido el aviso.
 */
export function estadoDeImpago(
  tenant: { subscriptionStatus: SubscriptionStatus; graceEndsAt?: string },
  ahora: number = Date.now(),
): ImpagoStatus {
  if (tenant.subscriptionStatus !== 'past_due') return { estado: 'al_dia', diasRestantes: 0 };
  const fin = tenant.graceEndsAt ? Date.parse(tenant.graceEndsAt) : NaN;
  if (!Number.isFinite(fin)) {
    return { estado: 'gracia', diasRestantes: DIAS_GRACIA_IMPAGO, graceEndsAt: tenant.graceEndsAt };
  }
  const restante = fin - ahora;
  if (restante <= 0) {
    return { estado: 'solo_lectura', diasRestantes: 0, graceEndsAt: tenant.graceEndsAt };
  }
  return {
    // Se redondea hacia arriba: con 30 h por delante quedan «2 días», no uno.
    estado: 'gracia',
    diasRestantes: Math.ceil(restante / 86_400_000),
    graceEndsAt: tenant.graceEndsAt,
  };
}

/** Fin del margen a partir del momento en que Stripe avisa del fallo. */
export function finDeGracia(desde: number = Date.now()): string {
  return new Date(desde + DIAS_GRACIA_IMPAGO * 86_400_000).toISOString();
}

export const PLANS: Record<PlanId, PlanDefinition> = {
  trial: {
    id: 'trial',
    name: 'Prueba',
    pricePerMonth: 0,
    assistantMessagesPerMonth: 20,
    // La prueba trae unos pocos escaneos a proposito: es la forma de que vean
    // lo que hace antes de que nadie les pida dinero por ello.
    receiptScansPerMonth: 10,
    maxLocations: 1,
    maxRecipes: 25,
    maxUsers: 1,
    features: ['Costeo de recetas', 'Cuenta de resultados', 'Asistente limitado'],
  },
  basic: {
    id: 'basic',
    name: 'Básico',
    pricePerMonth: 49,
    assistantMessagesPerMonth: 150,
    receiptScansPerMonth: 0,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 1,
    features: [
      '1 usuario (amplía con usuarios adicionales)',
      '1 local incluido (amplía por 9 €/mes cada uno)',
      'Recetas y subrecetas sin límite',
      'Precios con comisiones de delivery',
      'Cuenta de resultados y tesorería',
      'Cuentas por pagar',
      '150 preguntas al asistente al mes',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    pricePerMonth: 79,
    assistantMessagesPerMonth: 300,
    receiptScansPerMonth: 300,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Todo lo del plan Básico',
      '5 usuarios incluidos',
      '1 local incluido (amplía por 9 €/mes cada uno)',
      'Objetivos y periodificación de impuestos',
      'Menu engineering y alertas de recosteo',
      '300 preguntas al asistente al mes',
      'Escáner de facturas incluido (300 al mes)',
    ],
  },
  /**
   * Founder (cohorte fundadora, feedback 3 de Alex).
   *
   * 50 plazas, 39 €/mes con el precio BLOQUEADO mientras la suscripción siga
   * activa. Es Pro completo pero con 1 ubicación y 300 consultas/escaneos, y
   * SIN prueba gratuita (se paga desde el día 1). Si cancelan y vuelven,
   * entran al precio vigente — el precio Founder no se recupera. La cohorte de
   * 50 se controla desde Stripe/negocio, no en el código.
   */
  founder: {
    id: 'founder',
    name: 'Founder',
    pricePerMonth: 39,
    assistantMessagesPerMonth: 300,
    receiptScansPerMonth: 300,
    maxLocations: 1,
    maxRecipes: null,
    maxUsers: 5,
    features: [
      'Pro completo con 1 local incluido',
      'Precio de fundador bloqueado mientras sigas suscrito',
      '5 usuarios incluidos',
      '300 páginas escaneadas al mes (1 escaneo = 1 página)',
      '300 preguntas al asistente al mes',
      'Acceso anticipado a nuevas funciones',
      'Onboarding directo y canal privado de feedback',
    ],
  },
};

/**
 * Cohorte Founder CERRADA (Pol, 17 sep 2026). Nadie nuevo entra como Founder,
 * tenga el enlace o el código que tenga: la landing se retiró, la web ignora
 * la invitación y la API la rechaza. No cambia nada para quien ya es Founder
 * (conserva plan y precio bloqueado) ni para quien se dio de alta con la
 * invitación antes del cierre y aún no ha pagado (`founderPending`).
 * La API admite FOUNDER_COHORT_OPEN=true para reabrirla sin tocar código.
 */
export const FOUNDER_COHORT_OPEN = false;

export const FOUNDER_COHORT_CLOSED_MESSAGE =
  'La cohorte Founder ya está cerrada. Puedes empezar con la prueba gratuita de 14 días.';

/* ------------------------------------------------------------------ */
/* Ofertas por invitación                                              */
/* ------------------------------------------------------------------ */

/**
 * Oferta que llega por enlace (…/?invitacion=CODIGO) y no por un código
 * tecleado: el checkout mensual ya lleva el cupón de lanzamiento y Stripe no
 * deja cupón fijo y casilla de códigos en la misma sesión.
 *
 * ALEXFAMILY (Pol y Alex, 17 sep 2026): la comunidad de Alex contrata Pro al
 * precio de Básico durante un año, en mensual o en anual, si contrata antes
 * del 31 de octubre de 2026. Conserva la prueba de 14 días. NO se acumula con
 * la oferta de lanzamiento: la sustituye. Pasado el año, Stripe cobra Pro a
 * su precio sin que nadie haga nada.
 *
 * Los cupones tienen id fijo y los crea `infra/scripts/stripe-precios.mjs`,
 * limitados al producto Pro: en Básico no descuentan nada.
 */
export interface InvitationOffer {
  code: string;
  /** Cómo se le nombra al cliente. */
  name: string;
  plan: PlanId;
  /** Se cobra como este plan mientras dura. */
  pricedAs: PlanId;
  /** Meses que dura el precio rebajado desde que se contrata. */
  months: number;
  /** ISO. Último momento para contratar (fin del 31 oct en toda América). */
  redeemBy: string;
  /** Cupón mensual: `monthlyOff` cada mes durante `months` meses. */
  couponMonth: string;
  /** Cupón anual: `yearlyOff` de una vez, sobre el primer año. */
  couponYear: string;
}

export const INVITATION_QUERY_PARAM = 'invitacion';

export const INVITATION_OFFERS: Record<string, InvitationOffer> = {
  ALEXFAMILY: {
    code: 'ALEXFAMILY',
    name: 'Alex Family',
    plan: 'pro',
    pricedAs: 'basic',
    months: 12,
    redeemBy: '2026-11-01T08:00:00Z',
    couponMonth: 'alexia-alexfamily-mensual',
    couponYear: 'alexia-alexfamily-anual',
  },
};

/** La oferta de un código, si existe y aún se puede contratar. Sin distinguir mayúsculas. */
export function invitationOffer(code: string | undefined | null, now: number = Date.now()): InvitationOffer | null {
  const offer = INVITATION_OFFERS[(code ?? '').trim().toUpperCase()];
  if (!offer || now >= Date.parse(offer.redeemBy)) return null;
  return offer;
}

/** Lo que descuenta la oferta en cada factura del periodo (30 al mes · 270 el año). */
export function offerDiscount(offer: InvitationOffer, interval: BillingInterval = 'month'): number {
  return interval === 'year'
    ? pricePerYear(offer.plan) - pricePerYear(offer.pricedAs)
    : (PLANS[offer.plan]?.pricePerMonth ?? 0) - (PLANS[offer.pricedAs]?.pricePerMonth ?? 0);
}

/** Cupón de Stripe de la oferta para ese periodo. */
export function offerCoupon(offer: InvitationOffer, interval: BillingInterval = 'month'): string {
  return interval === 'year' ? offer.couponYear : offer.couponMonth;
}

/**
 * ¿Sigue pagando el precio rebajado? Solo en el plan de la oferta y durante
 * sus meses: si baja a Básico el cupón no aplica, y pasado el año caduca.
 * No mira `redeemBy`: eso es el plazo para contratar, no lo que dura.
 */
export function ofertaVigente(
  tenant: { plan: PlanId; offer?: { code: string; startedAt: string } },
  now: number = Date.now(),
): InvitationOffer | null {
  const offer = tenant.offer ? INVITATION_OFFERS[tenant.offer.code] : undefined;
  if (!offer || tenant.plan !== offer.plan) return null;
  const fin = new Date(tenant.offer!.startedAt);
  fin.setUTCMonth(fin.getUTCMonth() + offer.months);
  return now < fin.getTime() ? offer : null;
}

/** ¿El plan incluye el escáner de facturas de serie? (Pro y Founder). */
export function scannerIncluido(plan: PlanId): boolean {
  return (PLANS[plan]?.receiptScansPerMonth ?? 0) > 0 && (plan === 'pro' || plan === 'founder');
}

/**
 * Lo que un tenant aporta al MRR: plan + escaner suelto + asientos y centros
 * extra. Cuenta tambien a los `past_due` —siguen suscritos y Stripe los sigue
 * intentando cobrar—; solo salen del MRR al cancelar.
 *
 * Es una estimacion desde nuestros datos (los webhooks de Stripe): la cifra
 * exacta, con cupones y prorrateos, la da siempre Stripe.
 */
export function mrrDeTenant(tenant: {
  plan: PlanId;
  subscriptionStatus: string;
  addons?: readonly AddonId[];
  extraSeats?: number;
  extraLocations?: number;
  founderPending?: boolean;
  billingInterval?: BillingInterval;
  offer?: { code: string; startedAt: string };
}, ahora: number = Date.now()): number {
  // Un Founder que aun no ha pagado no es MRR, diga lo que diga el estado
  // de su suscripcion (un checkout con la tarjeta rechazada queda past_due).
  if (tenant.founderPending) return 0;
  if (tenant.subscriptionStatus !== 'active' && tenant.subscriptionStatus !== 'past_due') {
    return 0;
  }
  // Anual: 441 al año son 36,75 al mes, y los extras tambien van a 9 por 12.
  const periodo = tenant.billingInterval;
  // Con una oferta por invitación viva se cobra como el plan de abajo (Pro a 49).
  const oferta = ofertaVigente(tenant, ahora);
  const plan = planMrr(oferta ? oferta.pricedAs : tenant.plan, periodo);
  const escaner = (tenant.addons ?? []).includes('escaner')
    ? extraMrr(ADDONS.escaner.pricePerMonth, periodo)
    : 0;
  const asientos = Math.max(0, tenant.extraSeats ?? 0) * extraMrr(EXTRA_SEAT_PRICE_PER_MONTH, periodo);
  const centros =
    Math.max(0, tenant.extraLocations ?? 0) * extraMrr(EXTRA_LOCATION_PRICE_PER_MONTH, periodo);
  return Math.round((plan + escaner + asientos + centros) * 100) / 100;
}

/* ------------------------------------------------------------------ */
/* Coste del asistente                                                 */
/* ------------------------------------------------------------------ */

/**
 * Coste por millon de tokens del modelo del asistente, en euros.
 *
 * Es una estimacion para poder vigilar el margen sin salir de la app: la cifra
 * buena es siempre la factura de AWS. Se sobrescribe por variable de entorno
 * cuando cambien los precios de Bedrock, para no tener que desplegar codigo.
 */
export interface ModelPricing {
  inputPerMillion: number;
  outputPerMillion: number;
}

export const DEFAULT_MODEL_PRICING: ModelPricing = {
  inputPerMillion: 3,
  outputPerMillion: 15,
};

export interface AssistantUsage {
  whatsappCost?: number;
  transcribeCost?: number;
  textractCost?: number;
  tenantId: string;
  /** YYYY-MM */
  month: string;
  messages: number;
  /** Facturas escaneadas este mes. Cuota aparte de la del asistente. */
  scans: number;
  inputTokens: number;
  outputTokens: number;
  /** Coste estimado de las llamadas de texto a Bedrock, en euros. */
  bedrockCost?: number;
  /** Coste estimado de las transcripciones de OpenAI, en euros. */
  openAiCost?: number;
  /** Segundos de audio enviados a transcripción. */
  audioSeconds?: number;
  /** Grabaciones transcritas correctamente. */
  voiceRecipes?: number;
  /** Coste estimado acumulado del mes, en euros */
  estimatedCost: number;
  updatedAt: string;
}

export interface UsageStatus {
  month: string;
  plan: PlanId;
  planName: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  /** Primer dia del mes siguiente, cuando se reinicia el contador */
  resetsOn: string;
  estimatedCost: number;
  /** true si ya no puede preguntar mas este mes */
  exhausted: boolean;
  /** true a partir del 80 % del limite, para avisar antes de cortar */
  nearLimit: boolean;
}

export function estimateCost(
  inputTokens: number,
  outputTokens: number,
  pricing: ModelPricing = DEFAULT_MODEL_PRICING,
): number {
  const cost =
    (inputTokens / 1_000_000) * pricing.inputPerMillion +
    (outputTokens / 1_000_000) * pricing.outputPerMillion;
  return Math.round(cost * 10_000) / 10_000;
}

export function nextMonthStart(month: string): string {
  const [y, m] = month.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

export function usageStatus(
  plan: PlanId,
  usage: Pick<AssistantUsage, 'month' | 'messages' | 'estimatedCost'>,
): UsageStatus {
  const definition = PLANS[plan] ?? PLANS.trial;
  const limit = definition.assistantMessagesPerMonth;
  const used = usage.messages;
  const remaining = limit == null ? null : Math.max(0, limit - used);

  return {
    month: usage.month,
    plan: definition.id,
    planName: definition.name,
    used,
    limit,
    remaining,
    resetsOn: nextMonthStart(usage.month),
    estimatedCost: usage.estimatedCost,
    exhausted: limit != null && used >= limit,
    nearLimit: limit != null && used >= limit * 0.8,
  };
}

/** Mensaje que ve el duenyo cuando agota su cuota. Sin jerga ni culpa. */
export function quotaMessage(status: UsageStatus): string {
  const fecha = new Date(`${status.resetsOn}T00:00:00Z`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  return (
    `Has usado las ${status.limit} preguntas al asistente que incluye tu plan ${status.planName} este mes. ` +
    `El contador se reinicia el ${fecha}. Si te has quedado corto, puedes subir de plan desde Ajustes.`
  );
}

/* ------------------------------------------------------------------ */
/* Modulos de pago                                                     */
/* ------------------------------------------------------------------ */

export type AddonId = 'escaner';

export interface AddonDefinition {
  id: AddonId;
  name: string;
  /** Precio mensual sin IVA, en euros. */
  pricePerMonth: number;
  /** Escaneos incluidos al mes. */
  scansPerMonth: number;
  description: string;
}

/**
 * Escaner de facturas.
 *
 * Se cobra aparte en Basico y va incluido en Pro. La razon NO es el coste:
 * leer una factura cuesta alrededor de un centimo, asi que un cliente que
 * suba cien al mes cuesta un euro sobre una cuota de 49. Es una decision de
 * valor — dejar de picar facturas a mano es de las pocas cosas por las que un
 * restaurante paga sin pensarlo.
 *
 * Va como cuota mensual con escaneos incluidos, no por escaneo. Cobrar por
 * escaneo convierte cada foto en una decision ("¿merece la pena escanear un
 * ticket de 12 euros?") y el habito es justo lo que hace que el modulo siga
 * contratado el mes siguiente.
 */
export const ADDONS: Record<AddonId, AddonDefinition> = {
  escaner: {
    id: 'escaner',
    name: 'Escáner de facturas',
    pricePerMonth: 9,
    scansPerMonth: 150,
    description:
      'Sube una foto o PDF de una factura y Alexia extraerá los datos para proponerte el gasto: proveedor, fecha, importe sin IVA, IVA y total. Tú solo tienes que revisarlo y confirmar. Cada página cuenta como un escaneo.',
  },
};

/* ------------------------------------------------------------------ */
/* Asientos                                                            */
/* ------------------------------------------------------------------ */

/**
 * Asiento extra: 5 €/mes por usuario por encima de los que incluye el plan
 * (Basico 1 · Pro 5). No es un AddonId porque lleva cantidad — se contratan
 * N asientos, no un modulo que esta o no esta. En Stripe es una linea propia
 * cuya `quantity` acaba en `Tenant.extraSeats` via webhook.
 *
 * Decision de precio (Alex, 21 ago 2026): Basico se queda en 1 usuario a
 * proposito, pero el asiento extra es barato para que compartir contraseña
 * no compense — compartirla mata la trazabilidad de quien metio cada dato.
 */
export const EXTRA_SEAT_PRICE_PER_MONTH = 5;

/** Centro extra: 9 €/mes por cada centro por encima del incluido. */
export const EXTRA_LOCATION_PRICE_PER_MONTH = 9;

/** Asientos totales de un tenant: los del plan mas los contratados. */
export function seatLimitFor(plan: PlanId, extraSeats = 0): number {
  const delPlan = PLANS[plan]?.maxUsers ?? 1;
  return delPlan + Math.max(0, Math.floor(extraSeats));
}

/** Centros totales de un tenant: uno incluido en cualquier plan más los contratados. */
export function locationLimitFor(plan: PlanId, extraLocations = 0): number {
  const delPlan = PLANS[plan]?.maxLocations ?? 1;
  return delPlan + Math.max(0, Math.floor(extraLocations));
}

/**
 * Limite de centros de un tenant: el del plan, mas los contratados en Stripe,
 * mas los de cortesia regalados desde /admin (7 sep 2026). Es lo que deben
 * usar todas las comprobaciones; `locationLimitFor` a secas se queda para
 * simular una cantidad contratada distinta.
 */
export function locationLimitOf(
  tenant: { plan: PlanId; extraLocations?: number; bonusLocations?: number },
  extraLocations = tenant.extraLocations ?? 0,
): number {
  return locationLimitFor(tenant.plan, extraLocations + Math.max(0, Math.floor(tenant.bonusLocations ?? 0)));
}

/** Escaneos al mes de un tenant, sumando lo del plan y lo del modulo. */
export function scanLimitFor(plan: PlanId, addons: readonly AddonId[] = []): number {
  const delPlan = PLANS[plan]?.receiptScansPerMonth ?? 0;
  const delModulo = addons.includes('escaner') ? ADDONS.escaner.scansPerMonth : 0;
  return delPlan + delModulo;
}

/** ¿Tiene derecho a escanear? (aunque haya agotado la cuota del mes) */
export const puedeEscanear = (plan: PlanId, addons: readonly AddonId[] = []): boolean =>
  scanLimitFor(plan, addons) > 0;

/**
 * Lo que se le dice a quien quiere las facturas por correo sin tener escáner.
 * El correo es solo otra puerta al mismo escáner: sin él no hay nada que
 * procesar, así que la dirección ni se enseña ni se crea (Pol, 17 sep 2026).
 */
export function inboundEmailSinEscanerMessage(planName: string): string {
  return (
    `Las facturas por correo usan el escáner de facturas, y el plan ${planName} no lo incluye. ` +
    `Puedes contratarlo desde Ajustes → Plan y módulos por ${ADDONS.escaner.pricePerMonth} € al mes, ` +
    'o subir al plan Pro, que lo trae de serie.'
  );
}

export interface ScanQuota {
  used: number;
  limit: number;
  remaining: number;
  exhausted: boolean;
  nearLimit: boolean;
  /** true = ni siquiera tiene el modulo contratado. */
  sinModulo: boolean;
  resetsOn: string;
}

export function scanQuota(
  plan: PlanId,
  addons: readonly AddonId[],
  used: number,
  month: string,
): ScanQuota {
  const limit = scanLimitFor(plan, addons);
  return {
    used,
    limit,
    remaining: Math.max(0, limit - used),
    exhausted: limit > 0 && used >= limit,
    nearLimit: limit > 0 && used >= limit * 0.8,
    sinModulo: limit === 0,
    resetsOn: nextMonthStart(month),
  };
}

/** Lo que se le dice a quien no puede escanear. Sin jerga y con salida. */
export function scanQuotaMessage(quota: ScanQuota, planName: string): string {
  if (quota.sinModulo) {
    return (
      `El plan ${planName} no incluye el escáner de facturas. ` +
      `Puedes contratarlo desde Ajustes por ${ADDONS.escaner.pricePerMonth} € al mes ` +
      `(${ADDONS.escaner.scansPerMonth} escaneos), o subir al plan Pro, que lo incluye ` +
      `(${PLANS.pro.receiptScansPerMonth} escaneos al mes).`
    );
  }
  const fecha = new Date(`${quota.resetsOn}T00:00:00Z`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
  return (
    `Has usado los ${quota.limit} escaneos de este mes (cada página cuenta como uno). El contador se reinicia el ${fecha}. ` +
    'Mientras tanto puedes seguir registrando gastos manualmente.'
  );
}
